import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle } from "lucide-react";
import ProfileThumbnail from "../ProfileThumbnail";
import { PUBLIC_LISTING_CARD_CLASS, SectionTitle } from "../createEvent/CreateEventFormUi";
import { ReadableCard } from "../Sticker";
import { formatInstagramHandle, instagramHref } from "../../lib/instagram";
import { phoneToWaMe } from "../../lib/schoolFeeMessages";

const TEACHER_PREVIEW = 4;

const TEAM_LINK_CLASS =
  "inline-flex items-center gap-1 font-ui font-medium text-[13px] text-ink/55 hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm min-h-[44px]";

const SHOW_MORE_CLASS =
  "mt-0.5 font-ui font-medium text-[13px] text-ink/55 min-h-[44px] rounded-btn px-1 -ml-1 transition-colors hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2";

/**
 * Compact public school roster — leads (message) + teachers (Instagram).
 */
export default function SchoolPublicTeam({
  team = [],
  hideWhatsAppForIds = [],
}) {
  const [showAllTeachers, setShowAllTeachers] = useState(false);

  if (!team.length) return null;

  const hideWa = new Set(hideWhatsAppForIds.map(String));
  const leads = team.filter((p) => p.role === "ADMIN");
  const teachers = team.filter((p) => p.role !== "ADMIN");
  const showGroupTitles = leads.length > 0 && teachers.length > 0;
  const visibleTeachers = showAllTeachers
    ? teachers
    : teachers.slice(0, TEACHER_PREVIEW);
  const hiddenTeacherCount = Math.max(0, teachers.length - TEACHER_PREVIEW);

  return (
    <ReadableCard className={PUBLIC_LISTING_CARD_CLASS} data-testid="school-public-team">
      <SectionTitle>Who you&apos;ll meet</SectionTitle>

      {leads.length > 0 && (
        <TeamGroup
          title={showGroupTitles ? "School leads" : null}
          people={leads}
          showContact
          hideWhatsAppForIds={hideWa}
          testId="school-public-team-leads"
          className="pt-1"
        />
      )}
      {teachers.length > 0 && (
        <TeamGroup
          title={showGroupTitles ? "Teachers" : null}
          people={visibleTeachers}
          showContact={false}
          hideWhatsAppForIds={hideWa}
          testId="school-public-team-teachers"
          className={leads.length ? "pt-3" : "pt-1"}
          footer={
            hiddenTeacherCount > 0 ? (
              showAllTeachers ? (
                <button
                  type="button"
                  onClick={() => setShowAllTeachers(false)}
                  className={SHOW_MORE_CLASS}
                  data-testid="school-public-team-show-less"
                >
                  Show fewer
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAllTeachers(true)}
                  className={SHOW_MORE_CLASS}
                  data-testid="school-public-team-show-more"
                >
                  Show {hiddenTeacherCount} more
                </button>
              )
            ) : null
          }
        />
      )}
    </ReadableCard>
  );
}

function TeamGroup({
  title,
  people,
  showContact,
  hideWhatsAppForIds,
  testId,
  className,
  footer = null,
}) {
  return (
    <div className={className} data-testid={testId}>
      {title ? (
        <h3 className="font-ui font-semibold text-[12px] text-ink/65 mb-0.5">{title}</h3>
      ) : null}
      <ul className="divide-y divide-black/[0.06]">
        {people.map((person) => (
          <TeamPersonRow
            key={person.id}
            person={person}
            showContact={showContact && !hideWhatsAppForIds.has(String(person.id))}
          />
        ))}
      </ul>
      {footer}
    </div>
  );
}

function TeamPersonRow({ person, showContact }) {
  const igHref = instagramHref(person.instagram);
  const igLabel = formatInstagramHandle(person.instagram);
  const waHref = showContact ? phoneToWaMe(person.phone) : null;
  const profileHref = person.organizer_id ? `/host/${person.organizer_id}` : null;
  const firstName = person.name.split(/\s+/)[0] || "school";
  const nameClass =
    "font-ui font-semibold text-[14px] text-ink hover:text-plum focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm";

  return (
    <li className="py-2 first:pt-1.5" data-testid={`school-public-team-${person.id}`}>
      <div className="flex items-center gap-2.5">
        <ProfileThumbnail
          name={person.name}
          thumbnailUrl={person.photo_url}
          size="sm"
          className="bg-pinky shrink-0"
        />
        <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-2.5 gap-y-0">
          {profileHref ? (
            <Link
              to={profileHref}
              className={nameClass}
              data-testid={`school-public-team-profile-${person.id}`}
            >
              {person.name}
            </Link>
          ) : (
            <span className="font-ui font-semibold text-[14px] text-ink">{person.name}</span>
          )}
          {igHref && igLabel && (
            <a
              href={igHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Instagram: ${igLabel} (opens in a new tab)`}
              className={TEAM_LINK_CLASS}
              data-testid={`school-public-team-ig-${person.id}`}
            >
              <Instagram className="w-3.5 h-3.5" strokeWidth={2.25} aria-hidden />
              {igLabel}
            </a>
          )}
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Message ${person.name} on WhatsApp about classes (opens in a new tab)`}
              className={TEAM_LINK_CLASS}
              data-testid={`school-public-team-wa-${person.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.25} aria-hidden />
              Message {firstName}
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
